import { getUiMessages } from './pack';
import { getUiLocale } from './locale';
import { matchFailZh } from './failZhKeys';

const FAILED_KEY = 'error.common.failed';
const FAILED_ZH = '操作失败，请稍后重试';
const FAILED_EN = 'Operation failed. Please try again later.';
const NETWORK_KEY = 'error.common.network';
const NETWORK_ZH = '网络请求失败，请稍后重试';

function looksChinese(text: string) {
  return /[\u4e00-\u9fff]/.test(text);
}

function isTimeoutLike(err?: any, raw = '') {
  const status = Number(err?.response?.status || err?.request?.status || 0);
  if ([408, 502, 504].includes(status)) {
    return true;
  }
  const text = `${raw} ${err?.message || ''} ${err?.response?.statusText || ''}`.toLowerCase();
  return (
    text.includes('gateway_timeout') ||
    text.includes('gateway timeout') ||
    text.includes('econnaborted') ||
    text.includes('etimedout') ||
    /\b504\b/.test(text) ||
    (text.includes('timeout') && !text.includes('msgkey'))
  );
}

function interpolate(
  text: string,
  vars?: Record<string, string | number> | null
) {
  if (!vars) {
    return text;
  }
  let out = text;
  Object.keys(vars).forEach((name) => {
    out = out.split('{{' + name + '}}').join(String(vars[name]));
  });
  return out;
}

export type FailPayload = {
  message?: string;
  msgKey?: string;
  msgParams?: Record<string, string | number>;
};

export function resolveFailMessage(
  payload?: FailPayload | null,
  locale = getUiLocale(),
  messages = getUiMessages()
) {
  const raw = payload && payload.message != null ? String(payload.message) : '';
  const matched = matchFailZh(raw.trim());
  const key =
    (payload && payload.msgKey ? String(payload.msgKey) : '') ||
    (matched && matched.key) ||
    '';
  const params =
    (payload && payload.msgParams) ||
    (matched && matched.params) ||
    undefined;
  const generic =
    locale === 'zh-CN'
      ? messages[FAILED_KEY] || FAILED_ZH
      : messages[FAILED_KEY] && !looksChinese(messages[FAILED_KEY])
        ? messages[FAILED_KEY]
        : FAILED_EN;

  if (key) {
    const fromPack = messages[key];
    if (fromPack) {
      const translated = interpolate(fromPack, params);
      if (locale === 'zh-CN' || !looksChinese(fromPack)) {
        return translated;
      }
      return generic;
    }
    if (locale === 'zh-CN') {
      return interpolate(raw || generic, params);
    }
    return generic;
  }

  if (locale === 'zh-CN') {
    return raw || generic;
  }
  if (isTimeoutLike(undefined, raw)) {
    return messages['error.common.timeout'] && !looksChinese(messages['error.common.timeout'])
      ? messages['error.common.timeout']
      : 'The request timed out. Please try again later.';
  }
  if (!raw || looksChinese(raw)) {
    return generic;
  }
  return raw;
}

export function localizeFailPayload<T extends FailPayload>(payload: T): T {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }
  return {
    ...payload,
    message: resolveFailMessage(payload),
  };
}

export function failErrorMessage(payload?: FailPayload | null) {
  return resolveFailMessage(payload || { msgKey: FAILED_KEY, message: FAILED_ZH });
}

export function timeoutFailMessage() {
  return resolveFailMessage({
    msgKey: 'error.common.timeout',
    message: '系统超时，请稍后再试',
  });
}

export function networkFailMessage() {
  return resolveFailMessage({
    msgKey: NETWORK_KEY,
    message: NETWORK_ZH,
  });
}

export function toastFailMessage(err?: any) {
  if (err == null || err === '') {
    return resolveFailMessage({ msgKey: FAILED_KEY, message: FAILED_ZH });
  }
  if (typeof err === 'string') {
    if (isTimeoutLike(undefined, err)) {
      return timeoutFailMessage();
    }
    return resolveFailMessage({ message: err });
  }
  const payload =
    err.response?.data && typeof err.response.data === 'object'
      ? err.response.data
      : err;
  const raw = String(payload?.message || err.message || '');
  if (isTimeoutLike(err, raw)) {
    return timeoutFailMessage();
  }
  return resolveFailMessage({
    message: raw,
    msgKey: payload?.msgKey,
    msgParams: payload?.msgParams,
  });
}

export function isFailMessage(err: any, zh: string) {
  const matched = matchFailZh(zh);
  const key = matched && matched.key;
  const raw =
    typeof err === 'string'
      ? err
      : String(err?.message || err?.response?.data?.message || '');
  if (raw === zh) {
    return true;
  }
  if (key && (err?.msgKey === key || err?.response?.data?.msgKey === key)) {
    return true;
  }
  if (key && matchFailZh(raw.trim())?.key === key) {
    return true;
  }
  if (key) {
    return raw === resolveFailMessage({ message: zh, msgKey: key });
  }
  return false;
}
